import '../../styles/public-experience.css';
import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import SEOHead from './SEOHead';
import '../../styles/inner-pages.css';
import Breadcrumbs from './Breadcrumbs';
import { WebsiteSchema, OrganizationSchema } from './StructuredData';
import { usePageTracking } from '../hooks/useSEO';

// Lazy load pages for better performance
const ProfessionalHome = React.lazy(() => import('../../pages/ProfessionalHome2025'));
const ProfessionalAbout = React.lazy(() => import('../../pages/ProfessionalAbout'));
const FAQ = React.lazy(() => import('../../pages/FAQ'));
const Contact = React.lazy(() => import('../../pages/Contact'));
const ProfessionalSMEServices = React.lazy(() => import('../../components/SMEServices'));
const ProfessionalLargeEnterpriseServices = React.lazy(() => import('../../pages/ProfessionalLargeEnterpriseServices'));
const ProfessionalConsultancyServices = React.lazy(() => import('../../pages/ProfessionalConsultancyServices'));
const SMESuccessStories = React.lazy(() => import('../../pages/SMESuccessStories'));
const LargeEnterpriseSuccessStories = React.lazy(() => import('../../pages/LargeEnterpriseSuccessStories'));
const ConsultancySuccessStories = React.lazy(() => import('../../pages/ConsultancySuccessStories'));
const QuotePage = React.lazy(() => import('../../pages/QuotePage'));
const Blog = React.lazy(() => import('../../pages/Blog'));
const BlogPost = React.lazy(() => import('../../pages/BlogPost'));
const AdminLogin = React.lazy(() => import('../../admin/AdminLogin'));
const AdminDashboard = React.lazy(() => import('../../admin/AdminDashboard'));

// Loading component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]" role="status" aria-label="Loading page">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    <span className="sr-only">Loading...</span>
  </div>
);

// SEO-optimized route wrapper
const SEORoute = ({ children, path, ...seoProps }) => {
  return (
    <>
      <SEOHead {...seoProps} />
      {path && path !== '/' && !path.startsWith('/admin') ? <div className='gpm-inner-page' data-page={path}>{children}</div> : children}
    </>
  );
};

const SEORouter = () => {
  usePageTracking();
  
  return (
    <>
      {/* Global Schema Markup */}
      <WebsiteSchema />
      <OrganizationSchema />
      
      {/* Breadcrumbs - will auto-generate based on route */}
      <Breadcrumbs />
      
      {/* Main Routes */}
      <main id="main-content" className="flex-1 w-full">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route 
              path="/" 
              element={
                <SEORoute path="/">
                  <ProfessionalHome />
                </SEORoute>
              } 
            />
            
            {/* Redirect parent pages to homepage */}
            <Route path="/services" element={<Navigate to="/" replace />} />
            <Route path="/success-stories" element={<Navigate to="/" replace />} />
            
            <Route 
              path="/services/sme" 
              element={
                <SEORoute path="/services/sme">
                  <ProfessionalSMEServices />
                </SEORoute>
              } 
            />
            
            <Route
              path="/services/large-enterprise"
              element={
                <SEORoute path="/services/large-enterprise">
                  <ProfessionalLargeEnterpriseServices />
                </SEORoute>
              }
            />
            
            <Route
              path="/services/consultancy"
              element={
                <SEORoute path="/services/consultancy">
                  <ProfessionalConsultancyServices />
                </SEORoute>
              }
            />
            
            <Route 
              path="/success-stories/sme" 
              element={
                <SEORoute path="/success-stories/sme">
                  <SMESuccessStories />
                </SEORoute>
              } 
            />
            
            <Route
              path="/success-stories/large-enterprise"
              element={
                <SEORoute path="/success-stories/large-enterprise">
                  <LargeEnterpriseSuccessStories />
                </SEORoute>
              }
            />
            
            <Route
              path="/success-stories/consultancy"
              element={
                <SEORoute path="/success-stories/consultancy">
                  <ConsultancySuccessStories />
                </SEORoute>
              }
            />
            
            <Route 
              path="/quote" 
              element={
                <SEORoute path="/quote">
                  <QuotePage />
                </SEORoute>
              } 
            />
            
            <Route 
              path="/blog" 
              element={
                <SEORoute path="/blog">
                  <Blog />
                </SEORoute>
              } 
            />
            <Route 
              path="/blog/:slug" 
              element={
                <SEORoute path="/blog/:slug">
                  <BlogPost />
                </SEORoute>
              } 
            />

            {/* Admin routes (noindex) */}
            <Route 
              path="/admin/login" 
              element={
                <SEORoute 
                  path="/admin/login" 
                  title="Admin Login"
                  description="Sign in to manage blog content"
                  noindex={true}
                >
                  <AdminLogin />
                </SEORoute>
              } 
            />
            <Route 
              path="/admin" 
              element={
                <SEORoute 
                  path="/admin" 
                  title="Blog Admin"
                  description="Manage blog posts"
                  noindex={true}
                >
                  <AdminDashboard />
                </SEORoute>
              } 
            />
            
            <Route 
              path="/about" 
              element={
                <SEORoute path="/about">
                  <ProfessionalAbout />
                </SEORoute>
              } 
            />
            
            <Route 
              path="/faq" 
              element={
                <SEORoute path="/faq">
                  <FAQ />
                </SEORoute>
              } 
            />
            
            <Route 
              path="/contact" 
              element={
                <SEORoute path="/contact">
                  <Contact />
                </SEORoute>
              } 
            />
            
            {/* 404 Page */}
            <Route 
              path="*" 
              element={
                <SEORoute 
                  title="Page Not Found - Global Payroll Migration"
                  description="The page you're looking for doesn't exist. Return to our homepage or contact us for assistance."
                  noindex={true}
                >
                  <div className="ux-page ux-empty"><span className="ux-kicker">GPM / 404</span><h1>A little <em>off course.</em></h1><p>The page you're looking for doesn't exist.</p><a href="/" className="ux-button">Return Home ↗</a><p><a href="/contact">Contact our team</a></p></div>
                </SEORoute>
              } 
            />
          </Routes>
        </Suspense>
      </main>
    </>
  );
};

export default SEORouter;
